// src/lib/themes.ts -- Slice J / Phase 2.
//
// User-selectable skin definitions. Two themes:
//   * "warm-gold"    -- the current Info Hub palette renamed (no visual
//                       change for users who keep the default).
//   * "nymr-website" -- palette tokens extracted from the live nymr.co.uk
//                       site (compiled Bootstrap `app/brands/default/app.css`).
//
// The five tokens below are the ONLY ones the theme override touches at
// runtime -- they map to the css custom properties `--c-accent`,
// `--c-accent-bg`, `--c-bg`, `--c-ink`, `--c-link`. Components that read
// other heritage tokens (paperWarm, brassGlow, hairline, etc.) keep using
// the constants in src/theme.ts unchanged.
//
// Source (NYMR Website palette):
//   Extracted 2026-05-18 from https://www.nymr.co.uk/app/brands/default/app.css
//   The live site uses Bootstrap 5 with custom --bs-* overrides. The
//   five tokens map to: --bs-primary, --bs-secondary, --bs-body-bg,
//   --bs-body-color, --bs-link-color. The site's body font (Figtree) is
//   intentionally NOT migrated -- Slice J is colour-only (P2.6: "3-5
//   token overrides, not a full re-skin").

import { C } from "../theme";

export type ThemeKey = "warm-gold" | "nymr-website";

export interface ThemeTokens {
  accent: string;
  accentBg: string;
  bg: string;
  ink: string;
  link: string;
}

export const THEMES: Readonly<Record<ThemeKey, ThemeTokens>> = {
  "warm-gold": {
    // Current Info Hub palette, renamed. C.brass is the primary brass/amber;
    // C.brassDk is the closest analogue to a link colour (no separate link
    // token exists in the heritage system).
    accent:   C.brass,    // #A6771C
    accentBg: C.brassBg,  // #FBEFCB
    bg:       C.paper,    // #FBF6E8
    ink:      C.ink,      // #2B2014
    link:     C.brassDk,  // #7A540B
  },
  "nymr-website": {
    accent:   "#fd9c00",  // --bs-primary; orange/amber
    accentBg: "#003d1f",  // --bs-secondary; deep heritage green (headers/dark surfaces)
    bg:       "#e8e0d4",  // --bs-body-bg; warm cream / parchment
    ink:      "#00282a",  // --bs-body-color; near-black with green tint
    link:     "#da4c2d",  // --bs-link-color; terracotta
  },
};

export const THEME_KEYS: ReadonlyArray<ThemeKey> = ["warm-gold", "nymr-website"];

export const THEME_LABELS: Readonly<Record<ThemeKey, string>> = {
  "warm-gold":    "Warm Gold",
  "nymr-website": "NYMR Website",
};

export const DEFAULT_THEME: ThemeKey = "warm-gold";

// CSS custom-property names. Components consume these via var(--c-accent)
// fallbacks in src/theme.ts; useTheme writes them on document.documentElement.
export const THEME_CSS_VARS: Readonly<Record<keyof ThemeTokens, string>> = {
  accent:   "--c-accent",
  accentBg: "--c-accent-bg",
  bg:       "--c-bg",
  ink:      "--c-ink",
  link:     "--c-link",
};

export function getTheme(key: string | null | undefined): ThemeTokens {
  if (key && key in THEMES) return THEMES[key as ThemeKey];
  return THEMES[DEFAULT_THEME];
}

export function isThemeKey(v: unknown): v is ThemeKey {
  return typeof v === "string" && (THEME_KEYS as ReadonlyArray<string>).includes(v);
}
